import React, { useRef, useState } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Select } from '../Select';
import { TextInput } from '../TextInput';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  timeline: string;
  whatsappPreferred: boolean;
}
const LOCATIONS = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'Outside the United States'].map(location => ({
  label: location,
  value: location.toLowerCase().replaceAll(' ', '-')
}));
const TIMELINES = ['Within the next 30 days', 'Within 1 to 3 months', 'Within 3 to 6 months', 'Within 6 to 12 months', 'Just gathering information right now'].map(timeline => ({
  label: timeline,
  value: timeline.toLowerCase().replaceAll(' ', '-')
}));
const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  location: '',
  timeline: '',
  whatsappPreferred: false
};
const TRUST_CHIPS = ['✓ No money up front.', '✓ No commitment.', '✓ 10% deposit refundable in 7 days (processing up to 60 days).'];
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/zFfvFQI9459DpBfcybiY/webhook-trigger/170c50bc-cf36-4417-b739-0614603d0eae';
const MINIMUM_COMPLETION_TIME_MS = 3_000;
const SUBMISSION_COOLDOWN_MS = 60_000;
const LAST_SUBMISSION_KEY = 'pler-smile-last-form-submission';
export function ReserveForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formStartedAt = useRef(Date.now());
  const update = (key: Exclude<keyof FormState, 'whatsappPreferred'>) => (value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined
    }));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = 'Please enter your first name.';
    if (!form.lastName.trim()) next.lastName = 'Please enter your last name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (form.phone.trim().length < 7) next.phone = 'Enter a phone number for the clinical team to call.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    if (website) {
      setSubmitted(true);
      return;
    }
    const now = Date.now();
    if (now - formStartedAt.current < MINIMUM_COMPLETION_TIME_MS) {
      setSubmitError('Please take a moment to review your details, then try again.');
      return;
    }
    const lastSubmission = Number(window.localStorage.getItem(LAST_SUBMISSION_KEY) || '0');
    if (now - lastSubmission < SUBMISSION_COOLDOWN_MS) {
      setSubmitError('Please wait a minute before sending another request.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${form.firstName.trim()} ${form.lastName.trim()}`,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: `${form.firstName.trim()} ${form.lastName.trim()}`,
          email: form.email.trim(),
          phone: form.phone.trim(),
          location: form.location,
          timeline: form.timeline,
          whatsappPreferred: form.whatsappPreferred,
          source: 'Pler Smile Dadeland 5-Day Smile Experience',
          submittedAt: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error('Webhook request failed');
      window.localStorage.setItem(LAST_SUBMISSION_KEY, String(now));
      setSubmitted(true);
    } catch {
      setSubmitError('Your request could not be sent. Please try again or contact Pler Smile directly.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="reserve" className="w-full bg-[#A9DFE9]/25 py-20 lg:py-28" data-id="element-168" data-mp="1wupeczt2q5jc">
      <div className="mx-auto max-w-2xl px-6" data-id="element-169" data-mp="3hd9r0pytokvh">
        <div className="flex flex-col items-center text-center" data-id="element-170" data-mp="3hczz2wu7w3n5">
          <Badge variant="aqua" size="sm" className="landing-badge mb-4 uppercase tracking-wide" data-id="element-171" data-mp="3cdwv7fpz43gr">Reserve Your Diagnostic Call</Badge>
          <h2 className="text-3xl font-bold leading-tight text-[#1A1A2E] md:text-4xl" data-id="element-172" data-mp="37m5a3kpl6py0">
            Find out if the 5 Day Smile Experience is right for you.
          </h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-[#1A1A2E]/80" data-id="element-173" data-mp="2ynbhs6jaglq9">
            Submit your details below. The Dadeland clinical team contacts you within 24 hours for a
            free consultation.
          </p>
        </div>

        <div className="mt-10 rounded-3xl bg-white p-6 shadow-soft ring-1 ring-white/60 sm:p-8" data-id="element-174" data-mp="2op417osww5os">
          {submitted ? <div className="flex flex-col items-center py-10 text-center" data-id="element-175" data-mp="mxilreian4kp">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#A9DFE9]/40 text-[#1F6E7B]" data-id="element-176" data-mp="mveld6jlsnp6">
                <CheckCircleIcon size={32} data-id="element-177" data-mp="2cemkd52myvp7" />
              </span>
              <h3 className="mt-5 text-2xl font-bold text-[#1A1A2E]" data-id="element-178" data-mp="2cd1n37oxsynn">Your request is in.</h3>
              <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-[#1A1A2E]/80" data-id="element-179" data-mp="2h6xko91agjia">
                Thank you, {form.firstName || 'friend'}. The Dadeland clinical team will contact you
                within 24 hours to schedule your free 30 minute diagnostic call.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-180" data-mp="2v318nklkjvmb">
              <div className="absolute h-px w-px overflow-hidden whitespace-nowrap" aria-hidden="true" data-id="element-181" data-mp="303p0u9ljaii4">
                <label htmlFor="website" data-id="element-182" data-mp="2m4640cy6atl4">Leave this field empty</label>
                <input id="website" name="website" type="text" value={website} onChange={event => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" data-id="element-183" data-mp="2q4zsp8jfwcf2" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-184" data-mp="175icv2sdsi92">
                <TextInput label="First name" name="firstName" value={form.firstName} onChange={event => update('firstName')(event.target.value)} error={errors.firstName} autoComplete="given-name" data-id="element-185" data-mp="1278hos6nphng" />
                <TextInput label="Last name" name="lastName" value={form.lastName} onChange={event => update('lastName')(event.target.value)} error={errors.lastName} autoComplete="family-name" data-id="element-186" data-mp="1c7hhsh3rrn84" />
              </div>
              <TextInput label="Email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={event => update('email')(event.target.value)} error={errors.email} autoComplete="email" data-id="element-187" data-mp="jjbv3ot5no5n" />
              <TextInput label="Phone number" name="phone" type="tel" value={form.phone} onChange={event => update('phone')(event.target.value)} error={errors.phone} autoComplete="tel" data-id="element-188" data-mp="tdvf0yisj1o6" />
              <div className="flex flex-wrap gap-2" aria-label="Booking protections" data-id="element-189" data-mp="r0ww89g4edv">
                {TRUST_CHIPS.map(chip => <span key={chip} className="rounded-full border border-[#A9DFE9] bg-white px-3 py-1.5 text-xs font-medium text-[#1A1A2E]/70" data-id="element-190" data-mp="3gcfa7jynf7dw">{chip}</span>)}
              </div>
              <Select label="Where are you currently located?" name="location" placeholder="Select your state" options={LOCATIONS} value={form.location} onChange={event => update('location')(event.target.value)} data-id="element-191" data-mp="3ldw58yugbzmk" />
              <Select label="When are you hoping to have this done?" name="timeline" placeholder="Select a timeframe" options={TIMELINES} value={form.timeline} onChange={event => update('timeline')(event.target.value)} data-id="element-192" data-mp="35nanxry2jpw" />
              <Checkbox name="whatsappPreferred" label="WhatsApp preferred" checked={form.whatsappPreferred} onCheckedChange={checked => setForm(current => ({
            ...current,
            whatsappPreferred: checked
          }))} data-id="element-193" data-mp="cuoobhb0op0p" />
              <Button type="submit" size="lg" className="w-full" disabled={isSubmitting} aria-busy={isSubmitting} data-id="element-194" data-mp="26eys0c6qk1a3">Reserve My Free Consultation</Button>
              {submitError ? <p role="alert" className="text-center text-sm font-medium text-red-700" data-id="element-195" data-mp="21hp1et7m1jko">{submitError}</p> : null}
              <div className="space-y-1 text-center text-sm leading-relaxed text-[#1A1A2E]/70" data-id="element-196" data-mp="1senzz5towgnc">
                <p className="italic" data-id="element-197" data-mp="2p4x5x8x7soln">Your diagnostic call is free. It commits you to nothing. If you decide to move forward after the call, your 10% deposit secures your clinical week and is fully refundable for 7 days.</p>
                <p data-id="element-198" data-mp="2gbtmmjzw1hj3">The remaining balance follows the payment plan defined before travel and is governed by the treatment contract. Refund processing can take up to 60 days from the date of the request.</p>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}